import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import DirectionService from "../../../services/DirectionService";
import ServiceService from "../../../services/ServiceService";
import UserService from "../../../services/UserService";
import ProcessusService from "../../../services/smq/ProcessusService";
import ButtonYellow from "../component/BouttonYellow";

const EMPTY_FORM = {
  direction_id: null,
  service_id: null,
  responsable_id: null,
  responsable_name: "",
  note: "",
};

const sameId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

const responseData = (payload, label, allowEmpty = false) => {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (allowEmpty && payload && payload.status === false) {
    return [];
  }

  if (allowEmpty && payload && payload.status === 0) {
    return [];
  }

  throw new Error(payload?.message || `Impossible de charger ${label}`);
};

const directionOption = (direction, configuration = null) => {
  const code = String(direction?.code || "").trim();
  const name = String(direction?.name || "").trim();

  return {
    id: direction?.direction_id,
    label: [code, name].filter(Boolean).join(" — ") || "Direction sans nom",
    direction_code: code,
    direction_name: name,
    nc_direction_id: configuration?.nc_direction_id ?? null,
    responsable_id: configuration?.responsable_id ?? null,
    responsable_name: configuration?.responsable_name || "",
    service_id: configuration?.service_id ?? null,
    note: configuration?.note || "",
    configured:
      configuration?.nc_direction_id !== null &&
      configuration?.nc_direction_id !== undefined,
  };
};

const configuredDirectionOption = (configuration) =>
  directionOption(
    {
      direction_id: configuration?.direction_id,
      code: configuration?.direction_code,
      name: configuration?.direction_name,
    },
    configuration
  );

const initialDirectionOption = (initialValues) => {
  if (!initialValues?.direction_id) {
    return null;
  }

  return configuredDirectionOption({
    ...initialValues,
    nc_direction_id: initialValues.nc_direction_id ?? "configured",
  });
};

const serviceOption = (service) => {
  const code = String(service?.code || "").trim();
  const name = String(service?.name || "").trim();

  return {
    id: service?.service_id,
    label: [code, name].filter(Boolean).join(" — ") || "Service sans nom",
  };
};

const userOption = (user) => {
  const fullName = String(user?.name || "").trim();
  const composedName = [user?.first_name, user?.last_name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");

  return {
    id: user?.user_id,
    label: fullName || composedName || "Utilisateur sans nom",
  };
};

const DirectionAdd = ({
  initialValues = null,
  mode = "create",
  onClose,
  onSaved,
  open,
}) => {
  const fixedEditMode = mode === "edit";
  const [effectiveMode, setEffectiveMode] = useState(
    fixedEditMode ? "edit" : "create"
  );
  const [directionsOptions, setDirectionsOptions] = useState([]);
  const [servicesOptions, setServicesOptions] = useState([]);
  const [usersOptions, setUsersOptions] = useState([]);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [directionsError, setDirectionsError] = useState(null);
  const [servicesError, setServicesError] = useState(null);
  const [usersError, setUsersError] = useState(null);
  const [directionsLoading, setDirectionsLoading] = useState(false);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const directionsRequestRef = useRef(0);
  const servicesRequestRef = useRef(0);
  const usersRequestRef = useRef(0);
  const responsableSummaryRequestRef = useRef(0);
  const directionInputRef = useRef(null);
  const formDataRef = useRef(EMPTY_FORM);
  formDataRef.current = formData;

  const fetchDirectionsOptions = useCallback(async () => {
    const requestId = directionsRequestRef.current + 1;
    directionsRequestRef.current = requestId;
    setDirectionsError(null);
    setDirectionsLoading(true);

    try {
      const [directionsResult, configurationsResult] =
        await Promise.allSettled([
          DirectionService.getDirection(),
          ProcessusService.getDirections(),
        ]);

      if (requestId !== directionsRequestRef.current) {
        return;
      }

      let directionsLoadError = null;
      let configurationsLoadError = null;
      let configurations = [];
      let baseDirections = [];

      if (directionsResult.status === "fulfilled") {
        try {
          baseDirections = responseData(
            directionsResult.value,
            "les directions"
          );
        } catch (error) {
          directionsLoadError = error;
        }
      } else {
        directionsLoadError = directionsResult.reason;
      }

      if (configurationsResult.status === "fulfilled") {
        try {
          configurations = responseData(
            configurationsResult.value,
            "les configurations de directions",
            true
          );
        } catch (error) {
          configurationsLoadError = error;
        }
      } else {
        configurationsLoadError = configurationsResult.reason;
      }

      const configurationsByDirection = new Map(
        configurations.map((configuration) => [
          String(configuration.direction_id),
          configuration,
        ])
      );
      const options = baseDirections
        .map((direction) =>
          directionOption(
            direction,
            configurationsByDirection.get(String(direction.direction_id))
          )
        )
        .filter((option) => option.id !== null && option.id !== undefined);

      configurations.forEach((configuration) => {
        if (
          !options.some((option) =>
            sameId(option.id, configuration.direction_id)
          )
        ) {
          options.push(configuredDirectionOption(configuration));
        }
      });

      const fallbackOption = initialDirectionOption(initialValues);
      if (
        fallbackOption &&
        !options.some((option) => sameId(option.id, fallbackOption.id))
      ) {
        options.push(fallbackOption);
      }

      options.sort((left, right) => left.label.localeCompare(right.label, "fr"));
      setDirectionsOptions(options);

      const loadErrors = [];
      if (directionsLoadError) {
        loadErrors.push("la liste générale des directions");
      }
      if (configurationsLoadError) {
        loadErrors.push("les configurations SMQ");
      }
      setDirectionsError(
        loadErrors.length
          ? `Chargement incomplet : impossible de charger ${loadErrors.join(
              " et "
            )}.`
          : null
      );
    } catch (error) {
      if (requestId === directionsRequestRef.current) {
        const fallbackOption = initialDirectionOption(initialValues);
        setDirectionsOptions(fallbackOption ? [fallbackOption] : []);
        setDirectionsError(
          error?.message || "Impossible de charger les directions"
        );
      }
    } finally {
      if (requestId === directionsRequestRef.current) {
        setDirectionsLoading(false);
      }
    }
  }, [initialValues]);

  const fetchServicesOptions = useCallback(async (directionId, currentServiceId) => {
    const requestId = servicesRequestRef.current + 1;
    servicesRequestRef.current = requestId;
    setServicesError(null);
    setServicesLoading(true);

    try {
      const payload = await ServiceService.getDirectionByType(directionId);
      const options = responseData(payload, "les services", true)
        .map(serviceOption)
        .filter((option) => option.id !== null && option.id !== undefined);

      if (
        currentServiceId !== null &&
        currentServiceId !== undefined &&
        !options.some((option) => sameId(option.id, currentServiceId))
      ) {
        options.push({ id: currentServiceId, label: "Service actuel" });
      }

      if (requestId === servicesRequestRef.current) {
        setServicesOptions(options);
      }
    } catch (error) {
      if (requestId === servicesRequestRef.current) {
        setServicesOptions([]);
        setServicesError(error?.message || "Impossible de charger les services");
      }
    } finally {
      if (requestId === servicesRequestRef.current) {
        setServicesLoading(false);
      }
    }
  }, []);

  const fetchUsersOptions = useCallback(
    async (
      directionId,
      serviceId = null,
      currentResponsableId = null,
      currentResponsableName = ""
    ) => {
      const requestId = usersRequestRef.current + 1;
      usersRequestRef.current = requestId;
      setUsersError(null);
      setUsersLoading(true);

      try {
        const payload = serviceId
          ? await UserService.getUserByService(serviceId)
          : await UserService.getAllUserByDirection(directionId);
        const options = responseData(payload, "les responsables", true)
          .map(userOption)
          .filter((option) => option.id !== null && option.id !== undefined);
        const hasCurrentResponsable = options.some((option) =>
          sameId(option.id, currentResponsableId)
        );

        if (
          currentResponsableId !== null &&
          currentResponsableId !== undefined &&
          !hasCurrentResponsable
        ) {
          options.push({
            id: currentResponsableId,
            label:
              String(currentResponsableName || "").trim() ||
              "Responsable actuel",
          });
        }

        if (requestId === usersRequestRef.current) {
          setUsersOptions(options);
        }
      } catch (error) {
        if (requestId === usersRequestRef.current) {
          setUsersOptions([]);
          setUsersError(
            error?.message || "Impossible de charger les responsables"
          );
        }
      } finally {
        if (requestId === usersRequestRef.current) {
          setUsersLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const fallbackOption = initialDirectionOption(initialValues);
    setEffectiveMode(fixedEditMode ? "edit" : "create");
    setFormData({
      ...EMPTY_FORM,
      direction_id: initialValues?.direction_id ?? null,
      service_id: initialValues?.service_id ?? null,
      responsable_id: initialValues?.responsable_id ?? null,
      responsable_name: initialValues?.responsable_name || "",
      note: initialValues?.note || "",
    });
    setDirectionsOptions(fallbackOption ? [fallbackOption] : []);
    setServicesOptions(
      initialValues?.service_id
        ? [{ id: initialValues.service_id, label: "Service actuel" }]
        : []
    );
    setUsersOptions(
      initialValues?.responsable_id
        ? [
            {
              id: initialValues.responsable_id,
              label:
                String(initialValues?.responsable_name || "").trim() ||
                "Responsable actuel",
            },
          ]
        : []
    );
    setErrors({});
    setDirectionsError(null);
    setServicesError(null);
    setUsersError(null);
    setSaveError(null);
    setSaving(false);
    fetchDirectionsOptions();

    return () => {
      directionsRequestRef.current += 1;
      servicesRequestRef.current += 1;
      usersRequestRef.current += 1;
      responsableSummaryRequestRef.current += 1;
    };
  }, [fetchDirectionsOptions, fixedEditMode, initialValues, open]);

  useEffect(() => {
    if (
      !open ||
      effectiveMode !== "edit" ||
      !formData.responsable_id ||
      formData.service_id
    ) {
      return undefined;
    }

    const requestId = responsableSummaryRequestRef.current + 1;
    responsableSummaryRequestRef.current = requestId;
    const responsableId = formData.responsable_id;

    UserService.getUserSummaryByUserId(responsableId)
      .then((payload) => {
        const serviceId = payload?.data?.service_id;

        if (
          requestId === responsableSummaryRequestRef.current &&
          serviceId !== null &&
          serviceId !== undefined &&
          String(serviceId) !== "0"
        ) {
          setFormData((current) =>
            sameId(current.responsable_id, responsableId) &&
            !current.service_id
              ? { ...current, service_id: serviceId }
              : current
          );
        }
      })
      .catch(() => {
        // Le service reste facultatif si le résumé utilisateur est indisponible.
      });

    return () => {
      responsableSummaryRequestRef.current += 1;
    };
  }, [effectiveMode, formData.responsable_id, formData.service_id, open]);

  useEffect(() => {
    if (!open || !formData.direction_id) {
      servicesRequestRef.current += 1;
      setServicesOptions([]);
      setServicesLoading(false);
      return;
    }

    fetchServicesOptions(
      formData.direction_id,
      formDataRef.current.service_id
    );
  }, [fetchServicesOptions, formData.direction_id, open]);

  useEffect(() => {
    if (!open || !formData.direction_id) {
      usersRequestRef.current += 1;
      setUsersOptions([]);
      setUsersLoading(false);
      return;
    }

    fetchUsersOptions(
      formData.direction_id,
      formData.service_id,
      formDataRef.current.responsable_id,
      formDataRef.current.responsable_name
    );
  }, [
    fetchUsersOptions,
    formData.direction_id,
    formData.service_id,
    open,
  ]);

  const validate = () => {
    const nextErrors = {};

    if (!formData.direction_id) {
      nextErrors.direction_id = "La direction est obligatoire.";
    }
    if (formData.note.trim().length > 250) {
      nextErrors.note = "La note ne peut pas dépasser 250 caractères.";
    }

    setErrors(nextErrors);

    if (nextErrors.direction_id) {
      directionInputRef.current?.focus();
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (saving || !validate()) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const data = {
        responsable_id: formData.responsable_id,
        note: formData.note.trim() || null,
      };

      if (effectiveMode === "edit") {
        await ProcessusService.updateDirection(formData.direction_id, data);
      } else {
        await ProcessusService.createDirection({
          direction_id: formData.direction_id,
          ...data,
        });
      }

      onClose?.();
      onSaved?.({
        direction_id: formData.direction_id,
        mode: effectiveMode,
      });
    } catch (error) {
      setSaveError(error?.message || "Impossible d’enregistrer la direction");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose?.();
    }
  };

  const handleDirectionChange = (_, option) => {
    servicesRequestRef.current += 1;
    usersRequestRef.current += 1;
    responsableSummaryRequestRef.current += 1;
    setServicesError(null);
    setUsersError(null);
    setSaveError(null);

    const configured = Boolean(option?.configured);
    setEffectiveMode(configured ? "edit" : "create");
    setServicesOptions(
      configured && option.service_id
        ? [{ id: option.service_id, label: "Service actuel" }]
        : []
    );
    setUsersOptions(
      configured && option.responsable_id
        ? [
            {
              id: option.responsable_id,
              label:
                String(option.responsable_name || "").trim() ||
                "Responsable actuel",
            },
          ]
        : []
    );
    setFormData({
      ...EMPTY_FORM,
      direction_id: option?.id ?? null,
      service_id: configured ? option.service_id ?? null : null,
      responsable_id: configured ? option.responsable_id ?? null : null,
      responsable_name: configured ? option.responsable_name || "" : "",
      note: configured ? option.note || "" : "",
    });
    setErrors((current) => ({ ...current, direction_id: null, note: null }));
  };

  const selectedDirection =
    directionsOptions.find((option) =>
      sameId(option.id, formData.direction_id)
    ) || null;
  const selectedService =
    servicesOptions.find((option) => sameId(option.id, formData.service_id)) ||
    null;
  const selectedResponsable =
    usersOptions.find((option) =>
      sameId(option.id, formData.responsable_id)
    ) || null;

  const autocompleteSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "7px",
      fontSize: 13,
      minHeight: 42,
      py: 0,
    },
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      PaperProps={{
        component: "form",
        onSubmit: handleSave,
        sx: {
          borderRadius: "18px",
          maxWidth: 820,
          overflow: "hidden",
          width: "100%",
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: "rgba(15, 23, 32, 0.52)" },
        },
      }}
    >
      <DialogTitle
        sx={{
          alignItems: "center",
          borderBottom: "1px solid #cad7e5",
          color: "#10263d",
          display: "flex",
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: 18,
          fontWeight: 700,
          justifyContent: "space-between",
          px: 2.5,
          py: 1.7,
        }}
      >
        {effectiveMode === "edit"
          ? "Modifier la configuration de la Direction"
          : "Configurer Direction"}
        <Button
          color="error"
          disabled={saving}
          onClick={handleClose}
          size="small"
          startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
          sx={{
            backgroundColor: "#fff5f5",
            border: "1px solid #e9bfc1",
            color: "#9f181d",
            fontWeight: 700,
            textTransform: "none",
          }}
          variant="outlined"
        >
          Fermer
        </Button>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: "18px !important" }}>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}

        <Box sx={{ display: "grid", gap: 1.6 }}>
          <Box>
            <Typography
              component="label"
              htmlFor="smq-direction"
              sx={{ color: "#315272", fontSize: 10, fontWeight: 700 }}
            >
              DIRECTION *
            </Typography>
            <Autocomplete
              disabled={fixedEditMode || saving}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
              loading={directionsLoading}
              loadingText="Chargement des directions…"
              noOptionsText="Aucune direction disponible"
              onChange={handleDirectionChange}
              options={directionsOptions}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box
                    component="li"
                    key={key}
                    {...optionProps}
                    sx={{ alignItems: "center", display: "flex", gap: 1 }}
                  >
                    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                      {option.label}
                    </Box>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(errors.direction_id || directionsError)}
                  helperText={errors.direction_id || directionsError}
                  id="smq-direction"
                  inputRef={directionInputRef}
                  placeholder="Sélectionner une direction"
                  size="small"
                />
              )}
              sx={autocompleteSx}
              value={selectedDirection}
            />
          </Box>

          <Box
            component="fieldset"
            sx={{
              border: "1px solid #cad7e5",
              borderRadius: "8px",
              display: "grid",
              gap: 1.5,
              m: 0,
              minWidth: 0,
              p: { xs: 1.4, sm: 1.8 },
            }}
          >
            <Typography
              component="legend"
              sx={{
                color: "#10263d",
                fontSize: 12,
                fontWeight: 700,
                px: 0.8,
              }}
            >
              Responsable (facultatif)
            </Typography>

            <Box>
              <Typography
                component="label"
                htmlFor="smq-responsable-service"
                sx={{ color: "#315272", fontSize: 10, fontWeight: 700 }}
              >
                SERVICE DU RESPONSABLE
              </Typography>
              <Autocomplete
                disabled={!formData.direction_id || saving}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  sameId(option.id, value.id)
                }
                loading={servicesLoading}
                loadingText="Chargement des services…"
                noOptionsText="Aucun service disponible"
                onChange={(_, value) => {
                  setFormData((current) => ({
                    ...current,
                    service_id: value?.id ?? null,
                    responsable_id: null,
                    responsable_name: "",
                  }));
                }}
                options={servicesOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(servicesError)}
                    helperText={servicesError}
                    id="smq-responsable-service"
                    placeholder="Tous les services de la direction"
                    size="small"
                  />
                )}
                sx={autocompleteSx}
                value={selectedService}
              />
            </Box>

            <Box>
              <Typography
                component="label"
                htmlFor="smq-responsable"
                sx={{ color: "#315272", fontSize: 10, fontWeight: 700 }}
              >
                NOM DU RESPONSABLE
              </Typography>
              <Autocomplete
                disabled={!formData.direction_id || saving}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  sameId(option.id, value.id)
                }
                loading={usersLoading}
                loadingText="Chargement des responsables…"
                noOptionsText="Aucun responsable disponible"
                onChange={(_, value) => {
                  setFormData((current) => ({
                    ...current,
                    responsable_id: value?.id ?? null,
                    responsable_name: value?.label || "",
                  }));
                }}
                options={usersOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(usersError)}
                    helperText={usersError}
                    id="smq-responsable"
                    placeholder="Sélectionner un responsable"
                    size="small"
                  />
                )}
                sx={autocompleteSx}
                value={selectedResponsable}
              />
            </Box>
          </Box>

          <Box>
            <Typography
              component="label"
              htmlFor="smq-direction-note"
              sx={{ color: "#315272", fontSize: 10, fontWeight: 700 }}
            >
              NOTE
            </Typography>
            <Box
              aria-describedby="smq-direction-note-help"
              component="textarea"
              disabled={saving}
              id="smq-direction-note"
              maxLength={250}
              onChange={(event) => {
                setFormData((current) => ({
                  ...current,
                  note: event.target.value,
                }));
                setErrors((current) => ({ ...current, note: null }));
              }}
              placeholder="Informations complémentaires"
              rows={4}
              sx={{
                backgroundColor: saving ? "#f2f4f6" : "#fff",
                border: `1px solid ${errors.note ? "#d32f2f" : "#b8c8d8"}`,
                borderRadius: "7px",
                boxSizing: "border-box",
                color: "#10263d",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                lineHeight: 1.5,
                minHeight: 92,
                outline: "none",
                p: 1.2,
                resize: "vertical",
                transition: "border-color 150ms ease, box-shadow 150ms ease",
                width: "100%",
                "&::placeholder": { color: "#788b9d", opacity: 1 },
                "&:hover:not(:disabled)": { borderColor: "#10263d" },
                "&:focus": {
                  borderColor: errors.note ? "#d32f2f" : "#1976d2",
                  boxShadow: `0 0 0 1px ${
                    errors.note ? "#d32f2f" : "#1976d2"
                  }`,
                },
                "&:disabled": { color: "#788b9d", cursor: "not-allowed" },
              }}
              value={formData.note}
            />
            <Typography
              color={errors.note ? "error" : "text.secondary"}
              id="smq-direction-note-help"
              sx={{ display: "block", fontSize: 10, mt: 0.4, px: 1.2 }}
            >
              {errors.note || `${formData.note.length}/250 caractères`}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid #cad7e5",
          gap: 1.2,
          px: 2.5,
          py: 1.4,
        }}
      >
        <Button
          color="inherit"
          disabled={saving}
          onClick={handleClose}
          sx={{ color: "#10263d", fontWeight: 700, textTransform: "none" }}
        >
          Annuler
        </Button>
        <ButtonYellow disabled={saving} type="submit">
          {saving && <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />}
          {effectiveMode === "edit" ? "Enregistrer" : "Créer"}
        </ButtonYellow>
      </DialogActions>
    </Dialog>
  );
};

export default DirectionAdd;
